import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { Chart as ChartJS } from "chart.js";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import { generateColors } from "../../../utils/chartColors";

// Icons
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/Twitter";

// -----------------------------------------------------------
// 🚀 HELPER: Parse Audience Age Only
// -----------------------------------------------------------
const parseDemographics = (profile) => {
    // Strategy: Look for demographics in multiple places
    // 1. Root level (latest)
    // 2. Data history (latest valid entry)

    let demoObj = null;

    // Check Root
    if (profile.profileDemographics) {
        demoObj = profile.profileDemographics;
    }
    // Check YouTube Root
    else if (profile.channelDemographics?.ageGroup && Object.keys(profile.channelDemographics.ageGroup).length > 0) {
        demoObj = {
            // Map YouTube format to internal format
            audience_age: profile.channelDemographics.ageGroup || {}
        };
    }
    // Check History if Root is empty
    else if (profile.data && profile.data.length > 0) {
        // Sort by date/datetime descending
        const sortedData = [...profile.data].sort((a, b) => {
            const dateA = new Date(a.date || a.datetime || 0);
            const dateB = new Date(b.date || b.datetime || 0);
            return dateB - dateA;
        });

        const latestItem = sortedData.find(item =>
            (item.profileDemographics?.audience_gender_age || item.profileDemographics?.audienceGenderAge || item.profileDemographics?.audience_age || item.profileDemographics?.audienceAge) ||
            (item.channelDemographics?.ageGroup && Object.keys(item.channelDemographics.ageGroup).length > 0) // ✅ YouTube History Robust Check
        );

        if (latestItem) {
            if (latestItem.channelDemographics?.ageGroup) {
                demoObj = { audience_age: latestItem.channelDemographics.ageGroup };
            } else {
                demoObj = latestItem.profileDemographics;
            }
        }
    }

    if (!demoObj) return null;

    // Normalize Keys (snake_case vs camelCase)
    const audience_age = demoObj.audience_age || demoObj.audienceAge || {};

    // ✅ NEW: Fallback to consolidated audience_gender_age
    if (Object.keys(audience_age).length === 0) {
        const genderAge = demoObj.audience_gender_age || demoObj.audienceGenderAge || {};
        if (Object.keys(genderAge).length > 0) {
            // Aggregate keys like "F_18-24"
            Object.entries(genderAge).forEach(([key, value]) => {
                const parts = key.split('_');
                if (parts.length === 2) {
                    const age = parts[1]; // Suffix is Age
                    audience_age[age] = (audience_age[age] || 0) + value;
                } else {
                    // Handle case where key might be just "18-24" or malformed
                    // If it matches age pattern, treat as age
                    if (key.match(/\d+-\d+/)) {
                        audience_age[key] = (audience_age[key] || 0) + value;
                    }
                }
            });
        }
    }

    // Validation: Must have at least one valid metric
    if (Object.keys(audience_age).length === 0 && Object.keys(demoObj.audience_gender_age || demoObj.audienceGenderAge || {}).length === 0) {
        return null;
    }

    let totalAudience = 0;

    // --- 1. Process Gender-Age Data (Stacked Bar) ---
    // Extract Age Labels first
    const ageLabelsSet = new Set();

    // We need to parse keys like "F_18-24", "M_18-24"
    const parsedData = {
        F: {},
        M: {},
        U: {},
        T: {} // ✅ Total (for non-gendered data like YouTube)
    };

    // Helper: Parse dot or underscore
    const parseKey = (key) => {
        let gender = 'U';
        let age = key;

        const parts = key.includes('_') ? key.split('_') : key.split('.');
        if (parts.length >= 2) {
            // Heuristic: Gender is usually single char F/M/U
            if (["F", "M", "U"].includes(parts[0])) {
                gender = parts[0];
                age = parts[1];
            } else if (["F", "M", "U"].includes(parts[1])) {
                gender = parts[1];
                age = parts[0];
            }
        }

        // ✅ CLEANUP: Remove "age" prefix if present (e.g. "age35-44" -> "35-44")
        age = age.replace(/^age/i, "");

        return { gender, age };
    };

    // 1. First Pass: Collect all Age Labels and populate gender buckets
    // Support both new `audience_gender_age` and separate `audience_age` fallback
    // If we have `audience_gender_age`, use it for stacking.
    // If only `audience_age`, we can't stack (single Unknown dataset).

    const genderAge = demoObj.audience_gender_age || demoObj.audienceGenderAge || {};
    const hasGenderData = Object.keys(genderAge).length > 0;

    if (hasGenderData) {
        Object.entries(genderAge).forEach(([key, value]) => {
            const { gender, age } = parseKey(key);
            if (!parsedData[gender]) parsedData[gender] = {};
            parsedData[gender][age] = (parsedData[gender][age] || 0) + value;
            ageLabelsSet.add(age);
            totalAudience += value;
        });
    } else {
        // Fallback to simple `audience_age` (No Gender breakdown) -> Map to "Total" (new category)
        // Ensure 'T' exists in parsedData
        if (!parsedData.T) parsedData.T = {}; // Initialize if not present (though initialized below in logic, safer here or ensure structure matches)
        // Wait, parsedData was initialized with F, M, U. I should add T to initialization.

        Object.entries(audience_age).forEach(([ageKey, value]) => {
            // ✅ CLEANUP: Remove "age" prefix
            const age = ageKey.replace(/^age/i, "");
            parsedData.T = parsedData.T || {}; // Safety
            parsedData.T[age] = value;
            ageLabelsSet.add(age);
            totalAudience += value;
        });
    }

    const ageLabels = Array.from(ageLabelsSet).sort();

    // 2. Build Datasets
    const datasets = [];

    // Colors
    const colorMap = {
        F: { color: "#E91E63", label: "Female" }, // Pink
        M: { color: "#2196F3", label: "Male" },   // Blue
        U: { color: "#9E9E9E", label: "Gender Undetermined" }, // Gray
        T: { color: "#4CAF50", label: "Total" }   // Green (Placeholder, will be overridden by profile color)
    };

    ["M", "F", "U", "T"].forEach(gender => {
        // Only add dataset if it has data
        const data = ageLabels.map(label => parsedData[gender][label] || 0);
        const hasData = data.some(v => v > 0);

        if (hasData) {
            datasets.push({
                label: colorMap[gender].label,
                data: data,
                backgroundColor: colorMap[gender].color,
                borderColor: colorMap[gender].color,
                profilePictureUrl: profile.profilePictureUrl,
                profileId: profile.profileId || profile.id,
                platform: profile.platform || "instagram"
            });
        }
    });

    // Calc Max Label (Total per age group)
    let maxVal = -1;
    let maxLabel = "N/A";

    ageLabels.forEach(label => {
        const totalForAge =
            (parsedData.F[label] || 0) +
            (parsedData.M[label] || 0) +
            (parsedData.U[label] || 0);

        if (totalForAge > maxVal) {
            maxVal = totalForAge;
            maxLabel = label;
        }
    });

    const ageChartData = {
        labels: ageLabels,
        datasets: datasets
    };

    return {
        ageChartData,
        totalAudience,
        maxLabel,
        maxVal
    };
};



// -----------------------------------------------------------
// 🚀 COMPONENT
// -----------------------------------------------------------
function AudienceAgeChart({
    title,
    datasets,
    previousdatasets,
    isLoading,
    entitlements,
    isEntitled = true, // ✅ Added
    featureName = "Demographics Report", // ✅ Added
    planRequired = "pro" // ✅ Added
}) {
    console.log("DEBUG: Age Datasets:", JSON.stringify(datasets, null, 2));

    const chartData = useMemo(() => {
        // Fallback structure
        const emptyState = {
            labels: [],
            datasets: [],
            customMetrics: (datasets || []).map(p => ({
                profilePic: p.profilePictureUrl,
                name: p.label || p.name || "Unknown",
                platform: p.platform || "instagram",
                metricName: "Top Age Group",
                metricValue: "N/A",
                previousMetricValue: "N/A",
                isQualitative: true
            })),
            legendInfo: [],
            hasContent: false
        };

        try {
            if (!datasets || datasets.length === 0) return emptyState;

            // 1. Collect all unique Age Labels across ALL profiles
            const allLabelsSet = new Set();

            // Helper to parse individual profile
            const parsedProfiles = datasets.map((profile, index) => {
                if (!profile) return null;

                let parsed = null;
                try {
                    parsed = parseDemographics(profile);
                } catch (parseErr) {
                    console.error("❌ Error parsing demographics for profile:", profile?.profileId, parseErr);
                    return null;
                }

                if (!parsed) return null;
                parsed.ageChartData.labels.forEach(l => allLabelsSet.add(l));

                const profileName = profile.label || profile.name || profile.username || `Profile ${index + 1}`;

                return {
                    ...parsed,
                    profileId: profile.profileId || profile.id,
                    name: profileName,
                    originalProfile: profile
                };
            }).filter(Boolean);

            if (parsedProfiles.length === 0) return emptyState;

            // ✅ Generate Colors Locally for Custom Legend Consistency
            const profileIds = parsedProfiles.map(p => p.profileId);
            const profileColors = generateColors(profileIds.length, profileIds);

            // Assign colors to parsed profiles
            parsedProfiles.forEach((p, i) => {
                p.baseColor = profileColors[i];
            });

            const finalLabels = Array.from(allLabelsSet).sort();
            const finalDatasets = [];

            parsedProfiles.forEach((p, i) => {
                if (!p.ageChartData || !Array.isArray(p.ageChartData.datasets)) return;

                p.ageChartData.datasets.forEach(ds => {
                    let opacity = 1.0;
                    let genderLabel = "";

                    if (ds.label === "Male") {
                        opacity = 1.0;
                        genderLabel = "Male";
                    } else if (ds.label === "Female") {
                        opacity = 0.6;
                        genderLabel = "Female";
                    } else if (ds.label === "Total") { // ✅ New Total Case
                        opacity = 1.0;
                        genderLabel = ""; // No suffix for clean display
                    } else {
                        opacity = 0.3;
                        genderLabel = "Gender Undetermined"; // ✅ Updated Label
                    }

                    // convert hex to rgba
                    const hexToRgba = (hex, alpha) => {
                        try {
                            const r = parseInt(hex.slice(1, 3), 16);
                            const g = parseInt(hex.slice(3, 5), 16);
                            const b = parseInt(hex.slice(5, 7), 16);
                            if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(0,0,0,${alpha})`;
                            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                        } catch (e) {
                            return `rgba(0,0,0,${alpha})`;
                        }
                    };

                    const finalColor = hexToRgba(p.baseColor, opacity);

                    // ⬇️ NORMALIZATION
                    const valueMap = {};
                    p.ageChartData.labels.forEach((label, idx) => {
                        valueMap[label] = ds.data[idx];
                    });
                    const alignedData = finalLabels.map(label => valueMap[label] || 0);

                    finalDatasets.push({
                        label: `${p.name} - ${genderLabel}`,
                        data: alignedData,
                        // ✅ Explicitly set color to match custom legend
                        backgroundColor: finalColor,
                        borderColor: p.baseColor,
                        borderWidth: 1,

                        stack: p.profileId,
                        profileId: p.profileId,
                        profileName: p.name,
                        gender: genderLabel,
                        borderRadius: 4,
                    });
                });
            });

            // 4. Construct Metrics
            const customMetrics = parsedProfiles.map(p => {
                const prevProfile = Array.isArray(previousdatasets)
                    ? previousdatasets.find(prev => prev.profileId === p.profileId)
                    : null;

                let previousMaxLabel = "N/A";
                if (prevProfile) {
                    try {
                        const prevParsed = parseDemographics(prevProfile);
                        if (prevParsed) previousMaxLabel = prevParsed.maxLabel;
                    } catch (e) { /* ignore */ }
                }

                return {
                    profilePic: p.originalProfile.profilePictureUrl,
                    name: p.name,
                    platform: "instagram",
                    metricName: "Top Age Group",
                    metricValue: p.maxLabel,
                    previousMetricValue: previousMaxLabel,
                    isQualitative: true
                };
            });

            return {
                labels: finalLabels,
                datasets: finalDatasets,
                customMetrics,
                // ✅ Return profiles info for Legend
                legendInfo: parsedProfiles.map(p => ({
                    name: p.name,
                    color: p.baseColor,
                    platform: p.originalProfile.platform
                })),
                hasContent: true
            };
        } catch (err) {
            console.error("🔥 CRITICAL ERROR in AudienceAgeChart processing:", err);
            return emptyState;
        }

    }, [datasets, previousdatasets]); // ✅ Add dependency

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart shows the distribution of your audience by age group. It provides a demographic
                breakdown to help you understand the generational makeup of your followers.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>Higher bars</strong>: Represent the age groups that make up the largest portion of your audience. <br />
                - <strong>Lower bars</strong>: Represent age groups with fewer followers. <br />
                - <strong>Dominant Group</strong>: The tallest bar indicates your primary audience demographic. <br />
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Tailor content tone and style to your dominant age group. <br />
                - Identify if you are reaching your target demographic. <br />
                - Adjust ad targeting strategies based on actual audience data. <br />
                - Spot emerging younger or older audience segments.
            </Typography>
        </div>
    );

    if (isLoading) {
        return <MDBox p={3}><MDTypography>Loading age data...</MDTypography></MDBox>;
    }

    // Simplified No-Data Check
    const hasContent = chartData?.hasContent;

    // Disable Default Legend
    const chartOptions = {
        plugins: {
            legend: {
                display: false,
            }
        }
    };

    // Build Custom Legend
    const CustomLegend = (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            {chartData.legendInfo.map((info, idx) => {
                let Icon = InstagramIcon; // Default
                if (info.platform === 'facebook') Icon = FacebookRoundedIcon;
                else if (info.platform === 'youtube') Icon = YouTubeIcon;
                else if (info.platform === 'twitter') Icon = TwitterIcon;

                return (
                    <Grid item key={idx} display="flex" alignItems="center">
                        {/* Color Box */}
                        <MDBox
                            sx={{
                                width: 24,
                                height: 12,
                                backgroundColor: info.color,
                                borderRadius: '2px',
                                marginRight: 1
                            }}
                        />
                        {/* Icon */}
                        <Icon color="inherit" fontSize="small" sx={{ marginRight: 0.5, opacity: 0.7 }} />
                        {/* Name */}
                        <MDTypography variant="button" color="text" fontWeight="medium">
                            {info.name}
                        </MDTypography>
                    </Grid>
                );
            })}
        </Grid>
    );

    // Determine Y-Axis Title based on platform
    const platformSet = new Set((datasets || []).map(d => d.platform?.toLowerCase()));
    const isYouTube = platformSet.has("youtube");
    const yAxisTitle = isYouTube ? "Subscribers" : "Followers";

    return (
        <MDBox mt={3} mb={6}>
            <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={12}>
                    <VerticalBarChart
                        title={title}
                        description={tooltipContent}
                        chart={{
                            labels: chartData.labels,
                            datasets: chartData.datasets
                        }}
                        height="25rem"
                        entitlements={entitlements}
                        stacked={true}
                        customMetrics={chartData.customMetrics}
                        disableToggle={true}
                        overrideOptions={chartOptions}
                        customLegend={CustomLegend}
                        isEntitled={isEntitled}
                        isEmpty={!hasContent} // ✅ Trigger "No data" inside chart
                        featureName={featureName}
                        planRequired={planRequired}
                        yAxisTitle={yAxisTitle} // ✅ Pass dynamic Y-Axis Title
                    />
                </Grid>
            </Grid>
        </MDBox>
    );
}

AudienceAgeChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array.isRequired,
    previousdatasets: PropTypes.array, // ✅ Added PropType
    entitlements: PropTypes.object,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
};

export default AudienceAgeChart;
